import { computed, toValue } from 'vue';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/vue-query';
import { useToast } from '@/composables/useToast.js';
import { useSearchAwareParams } from '@/composables/useSearchAwareParams';
import { useIdentifierStore } from '@/stores/identifiers.js';

import api from './api';

export const useIdentifiersByAgencyId = (params) => {
  const { filters } = useIdentifierStore();
  const searchParams = useSearchAwareParams({
    searchTerm: () => filters.search_text,
    defaultParams: filters,
    transformParams: {
      sort_by: 'relevance',
      sort_order: 'desc',
    },
  });

  return useQuery({
    queryKey: ['agency-identifiers', searchParams, params],
    queryFn: async () => await api.getByAgencyId({ ...searchParams.value, ...params }),
  });
};

export const useInfiniteIdentifiersByAgencyId = (agencyId) => {
  const { filters } = useIdentifierStore();
  const searchParams = useSearchAwareParams({
    searchTerm: () => filters.search_text,
    defaultParams: filters,
    transformParams: {
      sort_by: 'relevance',
      sort_order: 'desc',
    },
  });

  return useInfiniteQuery({
    queryKey: ['agency-identifiers', searchParams, agencyId],
    enabled: computed(() => !!toValue(agencyId)),
    queryFn: async ({ pageParam = 1 }) =>
      await api.getByAgencyId({ ...searchParams.value, agency_id: toValue(agencyId), page: pageParam }),
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.meta?.pagination;
      if (!pagination) return undefined;
      const { page, total_pages } = pagination;
      return page < total_pages ? page + 1 : undefined;
    },
  });
};

export const useCreateIdentifier = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data) => api.createIdentifier(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agency-identifiers'] });
      queryClient.invalidateQueries({ queryKey: ['agencies'] });
      queryClient.invalidateQueries({ queryKey: ['agency'] });
      toast({
        heading: 'Identifier created successfully.',
        message: `${variables.kind} ${variables.value} was added to the agency`,
        color: 'success',
        icon: 'check-circle',
      });
    },
  });
};

export const useUpdateIdentifier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.updateIdentifier(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency-identifiers'] });
      queryClient.invalidateQueries({ queryKey: ['agencies'] });
      queryClient.invalidateQueries({ queryKey: ['agency'] });
    },
  });
};

export const usePreviewIdentifier = () => {
  return useMutation({
    mutationFn: (data) => api.previewIdentifier(data),
  });
};

export const useSearchAgencyIdentifiers = (selectFromResult = (item) => item) => {
  const queryClient = useQueryClient();
  return async ({ search = '' }) => {
    const searchParams = {
      search_text: search,
      sort_by: search.length ? 'relevance' : 'created_at',
      per_page: 60,
    };

    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['agency-identifiers', searchParams],
        queryFn: () => api.getAgencyIdentifiers(searchParams),
      });
      return data.data.map(selectFromResult);
    } catch {
      return [];
    }
  };
};
